// Centralised RBAC roles + access tiers for the ODIN1 Dashboard.
// Single source of truth — App.jsx route guards, DashboardSidebar nav, the Agents hub
// cards and the landing page all import from here.
// See docs/superpowers/specs/2026-10-06-feature-roles-design.md

// Canonical Entra ID app-role values (token `roles` claim): one role per feature.
// Casing is canonical; all comparisons in this module are case-insensitive.
export const ROLES = {
  ADMIN: 'Admin',
  PLATFORMS: 'Platforms',
  REPORTS: 'Reports',
  AGENT_AUDIT_REQUEST: 'Agent_AuditRequest',
  AGENT_TSA: 'Agent_TSA',
  AGENT_TENDER: 'Agent_Tender',
  ENGINEER_PERFORMANCE: 'Engineer_Performance',
  ONBOARDING: 'Onboarding',
  WALKROUND: 'WalkRound',
  RECONCILE: 'Reconcile',
  MANAGEMENT: 'Management',
};

// Admin is a superset: every tier is [Admin, <the feature's role>].
const withAdmin = (role) => [ROLES.ADMIN, role];
const union = (...tiers) => [...new Set(tiers.flat())];

// Access tiers — the only role lists the rest of the app should reference.
export const TIERS = {
  // Settings, Customer Mappings, Sync, Knowledge Base.
  ADMIN_ONLY: [ROLES.ADMIN],
  // The six platform pages and the platform dashboard at `/`.
  PLATFORMS: withAdmin(ROLES.PLATFORMS),
  // Reports (with its scanners) and Recommendations.
  REPORTS: withAdmin(ROLES.REPORTS),
  AUDIT_REQUEST: withAdmin(ROLES.AGENT_AUDIT_REQUEST),
  TSA: withAdmin(ROLES.AGENT_TSA),
  TENDER: withAdmin(ROLES.AGENT_TENDER),
  // The leaderboard page and the report agent.
  ENGINEER_PERFORMANCE: withAdmin(ROLES.ENGINEER_PERFORMANCE),
  ONBOARDING: withAdmin(ROLES.ONBOARDING),
  WALKROUND: withAdmin(ROLES.WALKROUND),
  // Anyone reaching the Reconcile page gets every control on it (`canAct` is not role-derived).
  RECONCILE: withAdmin(ROLES.RECONCILE),
  // One role, equal permissions (spec 2026-10-02 §3.1). Admin is here for support.
  MANAGEMENT: withAdmin(ROLES.MANAGEMENT),
};
// The /agents card hub: computed, never hand-kept. The hub filters cards by their own tier.
TIERS.AGENTS_HUB = union(TIERS.AUDIT_REQUEST, TIERS.TSA, TIERS.TENDER, TIERS.ENGINEER_PERFORMANCE);

const CANONICAL_BY_LOWER = Object.values(ROLES).reduce((acc, v) => {
  acc[v.toLowerCase()] = v;
  return acc;
}, {});

// Normalise a raw token `roles` array: drop unknown values (including the retired
// Members/Projects/Operations/Bids/Accounts/Sales), dedupe. Returns canonical role strings.
export function normalizeRoles(rawRoles = []) {
  const out = new Set();
  for (const r of rawRoles || []) {
    if (typeof r !== 'string') continue;
    const canonical = CANONICAL_BY_LOWER[r.toLowerCase()];
    if (canonical) out.add(canonical);
  }
  return [...out];
}

// True if any of the user's (raw) roles, once normalised, is in `allowedRoles`.
// Empty `allowedRoles` => any authenticated user.
export function roleMatchesAny(rawRoles = [], allowedRoles = []) {
  if (!allowedRoles || allowedRoles.length === 0) return true;
  const have = new Set(normalizeRoles(rawRoles).map((r) => r.toLowerCase()));
  return allowedRoles.some((r) => have.has(String(r).toLowerCase()));
}

// The `X-User-Department` header value. Knowledge-MCP validates it against a fixed list
// and only Admin reaches the Knowledge Base, so it is 'admin' for an Admin and the
// least-privileged 'members' for everyone else. Never derived from a feature role name.
export function deriveDepartment(rawRoles = []) {
  const isAdmin = (rawRoles || []).some((r) => typeof r === 'string' && r.toLowerCase() === 'admin');
  return isAdmin ? 'admin' : 'members';
}
